import type {CSSProperties, ReactNode} from "react";
import {createPortal} from "react-dom";
import type {ViewportRect} from "~/components/table/useTablePanelViewportRect";

/**
 * Renders an overlay inside the table panel when possible so hidden tabs (e.g. journey profile)
 * do not leave a stale `position: fixed` layer on `document.body`.
 */
export function renderTableEmptyStatePortal({
    overlay,
    viewportBoundsElement,
    boundsRect,
}: {
    overlay: ReactNode;
    viewportBoundsElement: HTMLElement | null;
    boundsRect: ViewportRect | null;
}): ReactNode {
    const containedInPanel = viewportBoundsElement !== null;

    if (containedInPanel) {
        return createPortal(overlay, viewportBoundsElement);
    }

    if (
        typeof document === "undefined"
        || boundsRect === null
    ) {
        return null;
    }

    return createPortal(overlay, document.body);
}

export function getTableEmptyStateOverlayStyle({
    viewportBoundsElement,
    boundsRect,
}: {
    viewportBoundsElement: HTMLElement | null;
    boundsRect: ViewportRect | null;
}): CSSProperties | undefined {
    if (viewportBoundsElement !== null) {
        return undefined;
    }

    if (boundsRect === null) {
        return undefined;
    }

    return {
        position: "fixed",
        top: boundsRect.top,
        left: boundsRect.left,
        width: boundsRect.width,
        height: boundsRect.height,
    };
}

export function canRenderTableEmptyStatePortal({
    viewportBoundsElement,
    boundsRect,
}: {
    viewportBoundsElement: HTMLElement | null;
    boundsRect: ViewportRect | null;
}): boolean {
    if (viewportBoundsElement !== null) {
        return true;
    }

    return boundsRect !== null;
}
